import { Button } from "@mui/material";
import { useFormik, Field, FormikProvider } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ReactComponent as RightArrowIcon } from "assets/right-arrow.svg";
import { InputField } from "components/InputField";
import {
  SLAPSHOT_FORGOT_PASSWORD_URL,
  SLAPSHOT_HOME_URL,
  SLAPSHOT_REGISTER_URL,
  SLAPSHOT_VERIFY_2FA_URL,
  SLAPSHOT_VERIFY_EMAIL_URL,
} from "constants/urls";

import styles from "./SlapshotLoginPage.module.scss";
import { AUTH_TOKEN, AUTH_USER } from "../../constants/constants";
import { login } from "../../services/LoginService";
import { getIpInfo } from "../../services/UserService";
import { getRedirectPath } from "../../utils/helper";
import { useFingerprintEventId } from "../../utils/hooks";

const SlapshotLoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const getFingerprintEventId = useFingerprintEventId();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email")
        .required("Email id is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values, { setStatus }) => {
      setIsLoading(true);
      // Always send ipinfo; the backend prefers Fingerprint when its event
      // resolves and falls back to these otherwise.
      const [eventId, ipInfo] = await Promise.all([
        getFingerprintEventId(),
        getIpInfo(),
      ]);
      login(
        values.email.toLowerCase(),
        values.password,
        eventId ?? undefined,
        ipInfo ?? undefined,
      )
        .then((res) => {
          if (res.two_factor_required) {
            navigate(`${SLAPSHOT_VERIFY_2FA_URL}${location.search}`, {
              state: {
                preAuthToken: res.pre_auth_token,
                redirectPath: getRedirectPath(location),
              },
            });
          } else if (!res.verification_required) {
            localStorage.setItem(AUTH_TOKEN, res.token);
            localStorage.setItem(AUTH_USER, JSON.stringify(res.data));
            const redirectPath = getRedirectPath(location);
            navigate(redirectPath);
          } else {
            navigate(`${SLAPSHOT_VERIFY_EMAIL_URL}${location.search}`, {
              state: { email: res.data.email, id: res.data.id },
            });
          }
        })
        .catch((err) => {
          setStatus(err.detail);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  const isDisabled = () => {
    return isLoading || !!formik.errors.email || !!formik.errors.password;
  };

  useEffect(() => {
    document.title = "Login | Slapshot";
  }, []);

  return (
    <FormikProvider value={formik}>
      <form className={styles.loginContainer} onSubmit={formik.handleSubmit}>
        <div className={styles.welcomeContainer}>
          <h3>
            Welcome to <span>Slapshot</span>
          </h3>
          <p>Sign in to access your workspace</p>
        </div>
        <div className={styles.formContainer}>
          {formik.status && (
            <div className={styles.formError}>{formik.status}</div>
          )}
          <div className={styles.inputContainer}>
            <Field
              name="email"
              component={InputField}
              placeholder="Andrew@example.com"
              label="Username"
            />
          </div>
          <div className={styles.inputContainer}>
            <Field
              name="password"
              component={InputField}
              placeholder="* * * * * * * * *"
              label="Password"
              type="password"
            />
            <Link
              className={styles.forgotPassword}
              to={SLAPSHOT_FORGOT_PASSWORD_URL}
            >
              Forgot password?
            </Link>
          </div>
        </div>
        <Button
          type="submit"
          disabled={isDisabled()}
          className="slapshotAuthButton"
        >
          Sign in
          <RightArrowIcon style={{ marginLeft: "12px" }} />
        </Button>
        <div className={styles.registerContainer}>
          <p>New to Slapshot?</p>
          <Link
            to={`${SLAPSHOT_REGISTER_URL}${location.search}`}
            className={styles.register}
          >
            Register here
          </Link>
        </div>
      </form>
    </FormikProvider>
  );
};

export default SlapshotLoginPage;
